import happy from './logo.png';
import './App.css';
import Navbar from './components/navbar';
import React, { Component, Suspense, lazy } from 'react';
import Carousel from './components/carousel';
import ButtonNav from './components/buttonNav';
import Main from './components/main';
import Footer from './components/footer';
import { getCategories } from './services/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import LoginModal from './components/loginModal';
import RegisterModal from './components/registerModal';
import CompanyRegisterModal from './components/companyRegisterModal';
import CookieConsent from './components/CookieConsent';
import { JsonLd } from 'react-schemaorg';
import HeroImage from './components/HeroImage';
import SEO from './components/SEO';
import { Navigate } from 'react-router-dom';
import { isAuthenticated, isCompanyUser, getUserProfile, getCompanyProfile } from './services/api';
import CompanyLoginModal from './components/companyLoginModal';
import { useLocation } from 'react-router-dom';
import AdBanner from './components/AdBanner';

// Lazy-loaded route components (loaded only when navigated to)
const EventDetail = lazy(() => import('./components/EventDetail'));
const CompanyDashboard = lazy(() => import('./components/CompanyDashboard'));
const AdminCompanyList = lazy(() => import('./components/AdminCompanyList'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const CompanyLanding = lazy(() => import('./components/CompanyLanding'));
const AdsLanding = lazy(() => import('./components/AdsLanding'));
const RegisterLanding = lazy(() => import('./components/RegisterLanding'));
const SellTicketsLanding = lazy(() => import('./components/SellTicketsLanding'));
const ErrorPage = lazy(() => import('./components/ErrorPage'));
const CityPage = lazy(() => import('./components/CityPage'));
const DubaiPage = lazy(() => import('./components/DubaiPage'));
const CompanyPublicPage = lazy(() => import('./components/CompanyPublicPage'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ChiSiamo = lazy(() => import('./components/ChiSiamo'));

// Componente per proteggere le rotte che richiedono autenticazione
const PrivateRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('token') !== null;
  
  if (!isLoggedIn) {
    // Reindirizza alla pagina di login se l'utente non è autenticato
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Componente per proteggere le rotte che richiedono autenticazione come azienda
const CompanyRoute = ({ children }) => {
  const [isCompany, setIsCompany] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkCompanyStatus = async () => {
      try {
        const companyStatus = await isCompanyUser();
        setIsCompany(companyStatus);
      } catch (error) {
        console.error('Errore durante la verifica dello stato azienda:', error);
        setIsCompany(false);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated()) {
      checkCompanyStatus();
    } else {
      setLoading(false);
      setIsCompany(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated() && isCompany ? children : <Navigate to="/" />;
};

// NUOVO COMPONENTE PER IL LAYOUT
const AppLayout = ({ children, onSearch, onReset, userProfile, companyProfile }) => {
  const location = useLocation();
  const isInvito = location.pathname.startsWith('/compleanno');

  // Notifica AdSense ad ogni cambio di route (necessario per React SPA)
  React.useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {}
  }, [location.pathname]);

  return (
    <>
      {!isInvito && (
        <Navbar
          logo={happy}
          title="Happy Events"
          onSearch={onSearch}
          onReset={onReset}
          userProfile={userProfile}
          companyProfile={companyProfile}
        />
      )}
      {children}
      {!isInvito && <Footer />}
    </>
  );
};

// Componente migliorato per gestire il reindirizzamento del profilo
const ProfileRouter = () => {
  const [loading, setLoading] = React.useState(true);
  const [userType, setUserType] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const determinaTipoUtente = async () => {
      try {
        // Verifica prima se c'è un token valido
        if (!isAuthenticated()) {
          setUserType('guest');
          setLoading(false);
          return;
        }

        // Controlla se abbiamo già il tipo di utente nel localStorage
        const storedUserType = localStorage.getItem('user_type');
        if (storedUserType) {
          setUserType(storedUserType);
          setLoading(false);
          return;
        }

        // Solo se non abbiamo informazioni nel localStorage, fai una chiamata API
        try {
          const isCompany = await isCompanyUser();
          setUserType(isCompany ? 'company' : 'user');
        } catch (apiError) {
          console.error('API error in determinaTipoUtente:', apiError);
          setError('Errore durante la verifica del tipo di utente');
          setUserType('user'); // Default a user in case of error
        }
      } catch (error) {
        console.error('Errore durante la determinazione del tipo di utente:', error);
        setError('Impossibile determinare il tipo di utente');
        setUserType('user');
      } finally {
        setLoading(false);
      }
    };

    determinaTipoUtente();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {error}. <a href="/" className="alert-link">Torna alla home</a>
      </div>
    );
  }

  if (userType === 'company') {
    return <Navigate to="/company-dashboard" replace />;
  } else if (userType === 'user') {
    return <Navigate to="/user-profile" replace />;
  } else {
    // Se l'utente non è autenticato
    return <Navigate to="/" replace />;
  }
};

// Componente principale dell'applicazione
class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      categories: [],
      selectedCategory: '',
      searchTerm: '',
      loading: true,
      error: null,
      buttonItems: [],
      userProfile: null,
      companyProfile: null,
      showLogin: false,
      showRegister: false,
      showCompanyRegister: false,
      showCookieConsent: true,
      showCompanyLogin: false,
      userLocation: null,
      sessionExpired: false,
    };
  }

  componentDidMount() {
    this.fetchCategories();
    this.fetchUserData();
    window.addEventListener('session-expired', this.handleSessionExpired);
    window.addEventListener('openLoginModal', this.handleShowLogin);
    window.addEventListener('openRegisterModal', this.handleShowRegister);
  }

  componentWillUnmount() {
    window.removeEventListener('session-expired', this.handleSessionExpired);
    window.removeEventListener('openLoginModal', this.handleShowLogin);
    window.removeEventListener('openRegisterModal', this.handleShowRegister);
  }

  handleSessionExpired = () => {
    this.setState({ sessionExpired: true, userProfile: null, companyProfile: null });
    setTimeout(() => this.setState({ sessionExpired: false }), 5000);
  };

  fetchUserData = async () => {
    // Verifica se l'utente è autenticato
    if (isAuthenticated()) {
      try {
        // Determina il tipo di utente e carica i dati appropriati
        const isCompany = await isCompanyUser();
        
        if (isCompany) {
          const companyData = await getCompanyProfile();
          this.setState({ companyProfile: companyData });
        } else {
          const userData = await getUserProfile();
          this.setState({ userProfile: userData });
        }
      } catch (error) {
        console.error('Errore durante il recupero dei dati utente:', error);
      }
    }
  };

  fetchCategories = async () => {
    try {
      this.setState({ loading: true, error: null });
      const categories = await getCategories();
      
      if (!categories || categories.length === 0) {
        this.setState({
          error: 'Nessuna categoria trovata',
          loading: false
        });
        return;
      }
      
      // Crea i pulsanti per le categorie
      const buttonItems = [
        { id: 'btnradio_tutti', label: 'Tutti', checked: true },
        ...categories.map((category, index) => ({
          id: `btnradio${index}`,
          label: category.name,
          checked: false
        }))
      ];

      this.setState({
        categories,
        buttonItems,
        selectedCategory: '',
        loading: false
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
      this.setState({
        error: 'Errore nel caricamento delle categorie',
        loading: false
      });
    }
  };

  handleCategoryChange = (category) => {
    const selected = category === 'Tutti' ? '' : category;
    this.setState({ selectedCategory: selected });
    const updatedButtons = this.state.buttonItems.map(button => ({
      ...button,
      checked: button.label === category
    }));
    this.setState({ buttonItems: updatedButtons });
  };

  handleSearch = (searchTerm) => {
    this.setState({ searchTerm });
  };

  handleReset = () => {
    if (this.state.categories.length > 0) {
      const firstCategory = this.state.categories[0].name;
      
      this.setState({ 
        selectedCategory: firstCategory,
        searchTerm: '' 
      });
      
      const updatedButtons = this.state.buttonItems.map(button => ({
        ...button,
        checked: button.label === firstCategory
      }));
      
      this.setState({ buttonItems: updatedButtons });
    }
  };

  // Funzioni per gestire i modali
  handleShowLogin = () => this.setState({ showLogin: true });
  handleCloseLogin = () => this.setState({ showLogin: false });
  handleShowRegister = () => this.setState({ showRegister: true });
  handleCloseRegister = () => this.setState({ showRegister: false });
  handleShowCompanyRegister = () => this.setState({ showCompanyRegister: true });
  handleCloseCompanyRegister = () => this.setState({ showCompanyRegister: false });
  handleShowCompanyLogin = () => this.setState({ showCompanyLogin: true });
  handleCloseCompanyLogin = () => this.setState({ showCompanyLogin: false });

  render() {
    const { selectedCategory, searchTerm, buttonItems, loading, error, userProfile, companyProfile, sessionExpired } = this.state;
    
    const websiteStructuredData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Happy Events",
      "url": "https://eventhappy.it",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://eventhappy.it/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    return (
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SEO
          title="Happy Events - Scopri i migliori eventi vicino a te"
          description="Trova e partecipa a eventi unici. Dalle feste locali ai grandi concerti, Happy Events è la tua guida per il divertimento."
          keywords="eventi, concerti, feste, divertimento, biglietti"
        />
        <JsonLd data={websiteStructuredData} />
        <CookieConsent />
        {sessionExpired && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: 'linear-gradient(135deg,#7C3AED,#EC4899)',
            color: '#fff', textAlign: 'center',
            padding: '0.75rem 1rem', fontSize: '0.9rem', fontWeight: 600,
            boxShadow: '0 4px 16px rgba(124,58,237,0.4)',
          }}>
            🔒 Sessione scaduta — effettua di nuovo il login
          </div>
        )}

        <AppLayout
          onSearch={this.handleSearch}
          onReset={this.handleReset}
          userProfile={userProfile}
          companyProfile={companyProfile}
        >
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 40, height: 40, borderRadius: '50%', border: '4px solid rgba(124,58,237,0.15)', borderTopColor: '#7C3AED', animation: 'spin 0.8s linear infinite' }} /></div>}>
          <Routes>
            {/* Rotta per la home page e altri eventi */}
            <Route
              path="/"
              element={
                <>
                  <HeroImage
                    ctaText="Esplora gli eventi"
                    ctaLink="#events-section"
                  />
                  <Carousel onLocationDetected={(loc) => this.setState({ userLocation: loc })} />
                  {/* Banner AdSense — orizzontale tra carousel e filtri categoria */}
                  <AdBanner
                    slot="9565763395"
                    format="horizontal"
                    style={{ margin: '0.5rem auto', maxWidth: 728 }}
                  />
                  <div id="events-section" className="container" style={{ paddingTop: '0.5rem' }}>
                    <ButtonNav
                      items={buttonItems}
                      onButtonClick={this.handleCategoryChange}
                    />
                  </div>
                  <Main
                    selectedCategory={selectedCategory}
                    searchTerm={searchTerm}
                    userLocation={this.state.userLocation}
                  />
                </>
              }
            />
            
            {/* Rotte protette e pubbliche */}
            
            <Route path="/eventi/:city" element={<CityPage />} />
            <Route path="/dubai" element={<DubaiPage />} />
            <Route path="/event/:slug" element={<EventDetail />} />
            <Route path="/user-profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
            <Route path="/company-dashboard" element={<CompanyRoute><CompanyDashboard /></CompanyRoute>} />
            <Route path="/admin/companies" element={<AdminCompanyList />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/company-landing" element={<CompanyLanding />} />
            <Route path="/aziende" element={<AdsLanding />} />
            <Route path="/vendi-biglietti" element={<SellTicketsLanding />} />
            <Route path="/registrati" element={<RegisterLanding />} />
            <Route path="/organizzatore/:id" element={<CompanyPublicPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/profile" element={<PrivateRoute><ProfileRouter /></PrivateRoute>} />
            <Route path="/HeroImage" element={<HeroImage />} />
            <Route path="/Carousel" element={<Carousel />} />
            <Route path="/ButtonNav" element={<ButtonNav />} />
            <Route path="/event-detail" element={<EventDetail />} />
            {/* Rotta per pagine non trovate */}
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          </Suspense>
        </AppLayout>

        {/* Modali */}
        <LoginModal
          show={this.state.showLogin}
          onHide={this.handleCloseLogin}
          onSwitchToRegister={this.handleShowRegister}
          onLoginSuccess={this.fetchUserData}
        />
        <RegisterModal
          show={this.state.showRegister}
          handleClose={this.handleCloseRegister}
          onHide={this.handleCloseRegister}
          onSwitchToLogin={() => { this.handleCloseRegister(); this.setState({ showLogin: true }); }}
        />
        <CompanyRegisterModal show={this.state.showCompanyRegister} handleClose={this.handleCloseCompanyRegister} />
        <CompanyLoginModal
          show={this.state.showCompanyLogin}
          onHide={this.handleCloseCompanyLogin}
          onSwitchToRegister={this.handleShowRegister}
          onLoginSuccess={this.fetchUserData}
        />
      </Router>
    );
  }
}

export default App;